import React, { useState } from 'react';
import './ControlPanel.css';
import {
  getResourceProbabilities,
  calculateAdjacencies,
  calculateCollisions,
  calculateImbalance,
  isWithinRange,
} from '../util';
import {
  GameSearch,
  DoneGameSearch,
  GameSearchOptions,
  DESERT_PLACEMENTS,
  IMBALANCES,
  ADJACENCIES,
  COLLISIONS,
  MIN_IMBALANCE,
} from '../game-info';
import HelpButton, { HelpText } from './HelpButton';
import SelectionList from './SelectionList';
import RangeSelector from './RangeSelector';

type ShownHelp
  = 'generate'
  | 'variant'
  | 'balance'
  | 'adjacency'
  | 'desert-placement'
  | 'stats'
  | 'collisions'
  | undefined;

interface Props {
  search: GameSearch;
  onUpdate: (opts: GameSearchOptions) => void;
  drawerClosed: boolean;
  onSetDrawerClosed: (c: boolean) => void;
}

export default function ControlPanel({
  search,
  onUpdate: sendUpdate,
  drawerClosed,
  onSetDrawerClosed: setDrawerClosed,
}: Props) {

  const [shownHelp, setShownHelp] = useState<ShownHelp>();
  const [optionsState, setOptions] = useState<GameSearchOptions | undefined>();
  const stats = search.is === 'done' ? getStats(search) : undefined;
  const options = optionsState ?? search;
  const minImbalance = MIN_IMBALANCE[options.variant];

  return (
    <div id="ui-outer" className={drawerClosed ? 'closed' : 'open'}>

      <button
        children={drawerClosed ? '«\u00A0controls' : '\u00A0»\u00A0'}
        onClick={() => setDrawerClosed(!drawerClosed)}
        className="drawer-button"
      />

      <div id="ui">

        <div className="ui-section">
          <div className="label">
            <h2>Generate a Board</h2>
            <HelpButton
              isOpen={shownHelp === 'generate'}
              onClick={op => setShownHelp(op ? 'generate' : undefined)}
            />
          </div>
          <HelpText
            text={<><p>This randomly generates a Catan board within the constraints of the parameters you set below.</p><p>Mathematically, the number of possible boards is huge. A brute force algorithm would take a long time. So instead, this uses an optimizing algorithm to zero in on better and better candidates over time using principles of mutation and natural selection.</p><p>Note that with some settings it can be hard or downright impossible to get a perfect match. If you're having trouble, consider widening your search to allow more possibilities.</p></>}
            isOpen={shownHelp === 'generate'}
          />
          <div className="controls">
          <button
            onClick={() => sendUpdate(options)}
            children="Generate"
            className="generate-button"
          />
          </div>
        </div>

        <div className="ui-section">
          <div className="label">
            <h2>Board Size</h2>
            <HelpButton
              isOpen={shownHelp === 'variant'}
              onClick={op => setShownHelp(op ? 'variant' : undefined)}
            />
          </div>
          <HelpText
            text={<p>This selects between standard 2-4 player or 5-6 player board sizes.</p>}
            isOpen={shownHelp === 'variant'}
          />
          <div className="controls">
            <SelectionList
              value={options.variant}
              onChange={variant => setOptions({ ...options, variant })}
              options={[
                { name: 'Normal (2-4 players)', value: 'normal' },
                { name: 'Jumbo (5-6 players)', value: 'jumbo' },
              ]}
            />
          </div>
        </div>

        <div className="ui-section">
          <div className="label">
            <h2>Desert Placement</h2>
            <HelpButton
              isOpen={shownHelp === 'desert-placement'}
              onClick={op => setShownHelp(op ? 'desert-placement' : undefined)}
            />
          </div>
          <HelpText
            text={<p>Deserts can be located on the board in concentric zones starting from the middle and expanding outward.</p>}
            isOpen={shownHelp === 'desert-placement'}
          />
          <div className="controls">
            <SelectionList
              value={options.desertPlacement}
              onChange={desertPlacement => setOptions({ ...options, desertPlacement })}
              options={DESERT_PLACEMENTS.slice()}
            />
          </div>
        </div>

        <div className="ui-section">
          <div className="label">
            <h2>Resource Imalance</h2>
            <HelpButton
              isOpen={shownHelp === 'balance'}
              onClick={op => setShownHelp(op ? 'balance' : undefined)}
            />
          </div>
          <HelpText
            text={<><p>This has to do with the average number of roll-probability "dots" per hex. For example if lumber has dice tokens of 12, 11, 6, and 9, then it has 12 total dots. Divide that by the four lumber hexes, and you have an average of three dots per hex.</p><p><em>Resource imbalance</em> is the total variance in this number across all resource types. For example an imbalance of 0.75 means that the most productive resource has a 0.75 higher dot-average-per-hex than the least.</p><p>Note that since brick and ore have fewer hexes, they'll necessarily have lower <em>overall</em> dot-counts on a perfectly balanced board, despite having equal <em>per-hex</em> dot-averages.</p><p>Note also that 0.33 seems to be the theoretical minimum here, at least for normal board sizes. If you get stuck, try relaxing this setting.</p></>}
            isOpen={shownHelp === 'balance'}
          />
          <div className="controls">
            <RangeSelector
              warningCondition={([, hi]) => hi < minImbalance ? `A maximum imbalance lower than ${minImbalance} is mathematically impossible for ${options.variant} sized boards. Consider increasing the maximum.` : undefined}
              value={options.imbalance}
              onChange={imbalance => setOptions({ ...options, imbalance })}
              options={IMBALANCES.map(({ imbalance, label }) =>
                ({ label, value: imbalance })
              )}
            />
          </div>
        </div>

        <div className="ui-section">
          <div className="label">
            <h2>Adjacency Count</h2>
            <HelpButton
              isOpen={shownHelp === 'adjacency'}
              onClick={op => setShownHelp(op ? 'adjacency' : undefined)}
            />
          </div>
          <HelpText
            text={<p>Count the number of times a land hex of any type touches another of the same type. This is your <em>adjacency count</em>.</p>}
            isOpen={shownHelp === 'adjacency'}
          />
          <div className="controls">
            <RangeSelector
              value={options.adjacencies}
              onChange={adjacencies => setOptions({ ...options, adjacencies })}
              options={ADJACENCIES.map(({ adjacencies, label }) =>
                ({ label, value: adjacencies })
              )}
            />
          </div>
        </div>

        <div className="ui-section">
          <div className="label">
            <h2>In-Resource Duplicates</h2>
            <HelpButton
              isOpen={shownHelp === 'collisions'}
              onClick={op => setShownHelp(op ? 'collisions' : undefined)}
            />
          </div>
          <HelpText
            text={<p>Find the difference A - B, where A is the total number of land hexes (excluding deserts), and B is the number of unique resource/roll combinations. If this number is above zero, it means a number occurs more than once on a resource type.</p>}
            isOpen={shownHelp === 'collisions'}
          />
          <div className="controls">
            <RangeSelector
              value={options.collisions}
              onChange={collisions => setOptions({ ...options, collisions })}
              options={COLLISIONS.map(({ collisions, label }) =>
                ({ label, value: collisions })
              )}
            />
          </div>
        </div>

        {stats &&
          <div className="ui-section">
            <div className="label">
              <h2>Current Board Stats</h2>
              <HelpButton
                isOpen={shownHelp === 'stats'}
                onClick={op => setShownHelp(op ? 'stats' : undefined)}
              />
            </div>
            <HelpText
              text={<p>Stats about the board that was generated. If anything is crossed out, it means that parameter fell outside the parameters, due to the algorithm being unable to find a perfect match.</p>}
              isOpen={shownHelp === 'stats'}
            />
            <div className="controls">
              <ul id="stats">
                <li>Generations: <strong>{stats.generations}</strong></li>
                <li className={stats.imbalance.isBad ? 'bad' : 'good'}>Imbalance: <strong>{stats.imbalance.stat.toFixed(2)}</strong></li>
                <li className={stats.adjacencies.isBad ? 'bad' : 'good'}>Adjacencies: <strong>{stats.adjacencies.stat}</strong></li>
                <li className={stats.collisions.isBad ? 'bad' : 'good'}>Duplicates: <strong>{stats.collisions.stat}</strong></li>
                { Object.entries(getResourceProbabilities(stats.hexes)).map(([resource, probability]) =>
                  <li key={resource}>Total probability {resource}: <strong>{probability}</strong></li>
                ) }
              </ul>
            </div>
          </div>
        }

        <div className="ui-section">
          <div className="label">
            <h2>About Myself</h2>
          </div>
          <HelpText
            text={<>
              <p>
                My name is Greg and my hobby is to make stuff like this and post it on the web. Other things I've made:
              </p>
              <ul>
                <li><ExternLink href="https://greim.github.io/machine-planet/">An ominous white noise generator</ExternLink>.</li>
                <li><ExternLink href="https://greim.github.io/electron-golf/">A subatomic video game</ExternLink>.</li>
                <li><ExternLink href="https://greim.github.io/elmsweeper/">A minesweeper clone</ExternLink>.</li>
                <li><ExternLink href="https://greim.github.io/gaming-audio-reactions/">A sound effects pad</ExternLink>.</li>
              </ul>
              <p>
                Made with React and TypeScript.
                Follow me on <ExternLink href="https://twitter.com/greim">Twitter</ExternLink> or <ExternLink href="https://github.com/greim">GitHub</ExternLink>.
              </p>
            </>}
            isOpen
            showTitle={false}
          />
        </div>

      </div>
    </div>
  );
}

function ExternLink({ href, children }: { href: string, children: React.ReactNode }) {
  return <a
    href={href}
    rel="noopener noreferrer"
    target="_blank"
    children={children}
  />;
}

function getStats(search: DoneGameSearch) {
  const imbalStat = calculateImbalance(search.hexes, search.variant);
  const adjStat = calculateAdjacencies(search.hexes, search.variant);
  const collStat = calculateCollisions(search.hexes);
  const [imbalLo, imbalHi] = search.imbalance;
  const [adjLo, adjHi] = search.adjacencies;
  const [collLo, collHi] = search.collisions;
  const imbalIsBad = !isWithinRange(imbalStat, imbalLo, imbalHi, 'imbalance');
  const collIsBad = !isWithinRange(collStat, collLo, collHi, 'collisions');
  const adjIsBad = !isWithinRange(adjStat, adjLo, adjHi, 'adjacencies');
  return {
    imbalance: {
      stat: imbalStat,
      isBad: imbalIsBad,
    },
    adjacencies: {
      stat: adjStat,
      isBad: adjIsBad,
    },
    collisions: {
      stat: collStat,
      isBad: collIsBad,
    },
    hexes: search.hexes,
    generations: search.generations,
  };
}
